import React from 'react';
import { BriefcaseIcon, MapPinIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { usePortfolioData } from '../context/PortfolioDataContext';
import SectionWithNeuralBg from './SectionWithNeuralBg';

export default function Portfolio() {
  const { data } = usePortfolioData();
  const portfolio = data.portfolio;

  return (
    <SectionWithNeuralBg id="portfolio" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary-900/30 rounded-full flex items-center justify-center border border-primary-800/50">
              <BriefcaseIcon className="w-8 h-8 text-primary-400" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Experience
          </h2>
          <p className="text-lg text-gray-400">
            My professional journey building enterprise solutions
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          {portfolio.map((experience, index) => (
            <div
              key={index}
              className="relative pl-8 pb-12 border-l-2 border-gray-800 last:pb-0 group">
              {/* Timeline dot */}
              <div className="absolute left-0 top-0 transform -translate-x-[9px] w-4 h-4 rounded-full bg-primary-600 border-4 border-gray-900 group-hover:scale-125 transition-transform duration-300"></div>

              {/* Card */}
              <div className="card hover:shadow-xl hover:shadow-primary-900/20">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                  <div className="flex items-start gap-4">
                    {/* Company Logo */}
                    <a
                      href={experience.companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 group">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden group-hover:shadow-lg group-hover:scale-105 transition-all duration-300">
                        <img
                          src={experience.companyLogo}
                          alt={experience.company}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            // Fallback to company initial if logo fails
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full flex items-center justify-center text-gray-800 font-bold text-lg';
                            fallback.textContent = experience.company.charAt(0);
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      </div>
                    </a>

                    {/* Company & Role */}
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-1">
                        {experience.role}
                      </h3>
                      <a
                        href={experience.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-400 font-medium text-lg hover:text-primary-300 transition-colors">
                        {experience.company}
                      </a>
                    </div>
                  </div>

                  {/* Current Badge */}
                  {experience.current && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-900/50 text-green-300 text-sm font-medium border border-green-800">
                      Current
                    </span>
                  )}
                </div>

                {/* Meta Info */}
                <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{experience.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="w-4 h-4" />
                    <span>{experience.location}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed">
                  {experience.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionWithNeuralBg>
  );
}
